<template>
  <div class="request-affordability-bar">
    <div
      v-if="loading && isCalculated"
      class="text-center">
      <coozzy-spinner class="m-3" />
    </div>
    <div
      v-else-if="isCalculated"
      class="affordability-bar-wrapper"
      :class="{ 'affordability-bar-wrapper--vertical': vertical }">
      <div
        class="bar-track"
        :class="{ 'bar-track-vertical': vertical }"
        :style="trackStyle">
        <div
          class="bar-marker"
          :class="{ 'bar-marker-vertical': vertical }"
          :style="markerStyle" />
      </div>
      <div
        class="bar-label"
        :class="{ 'bar-label-vertical': vertical }"
        :style="vertical ? null : labelStyle">
        {{ percentLabel }}%
      </div>
    </div>
    <div
      v-else
      class="affordability-text">
      -
    </div>
  </div>
</template>

<script>
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'
import {
  AFFORDABILITY_COLORS,
  getBarSegments,
  clampPercent,
  isAffordabilityCalculated,
  affordabilityPercentForRentUnit
} from '@/misc/utils/requestAffordability'

export default {
  name: 'RequestAffordabilityBar',
  components: {
    CoozzySpinner
  },
  props: {
    affordability: {
      type: Object,
      default: null
    },
    settings: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    vertical: {
      type: Boolean,
      default: false
    },
    rentUnit: {
      type: String,
      default: null
    }
  },
  computed: {
    isCalculated() {
      return isAffordabilityCalculated(this.affordability?.status)
    },
    correctedPercent() {
      if (!this.isCalculated) {
        return 0
      }
      return affordabilityPercentForRentUnit(this.affordability.percent, this.rentUnit)
    },
    percent() {
      return Math.max(0, Math.min(100, this.correctedPercent))
    },
    displayPercent() {
      return this.correctedPercent
    },
    percentLabel() {
      return Number(this.displayPercent).toFixed(1)
    },
    segments() {
      return getBarSegments(this.settings)
    },
    trackStyle() {
      const { green, orange } = this.segments
      const greenEnd = green
      const orangeEnd = green + orange

      if (this.vertical) {
        return {
          background: `linear-gradient(
            to top,
            ${AFFORDABILITY_COLORS.green} 0%,
            ${AFFORDABILITY_COLORS.green} ${greenEnd}%,
            ${AFFORDABILITY_COLORS.orange} ${greenEnd}%,
            ${AFFORDABILITY_COLORS.orange} ${orangeEnd}%,
            ${AFFORDABILITY_COLORS.red} ${orangeEnd}%,
            ${AFFORDABILITY_COLORS.red} 100%
          )`
        }
      }

      return {
        background: `linear-gradient(
          to right,
          ${AFFORDABILITY_COLORS.green} 0%,
          ${AFFORDABILITY_COLORS.green} ${greenEnd}%,
          ${AFFORDABILITY_COLORS.orange} ${greenEnd}%,
          ${AFFORDABILITY_COLORS.orange} ${orangeEnd}%,
          ${AFFORDABILITY_COLORS.red} ${orangeEnd}%,
          ${AFFORDABILITY_COLORS.red} 100%
        )`
      }
    },
    markerStyle() {
      if (this.vertical) {
        return { bottom: `${clampPercent(this.percent)}%` }
      }
      return { left: `${clampPercent(this.percent)}%` }
    },
    labelStyle() {
      return { left: `${clampPercent(this.percent)}%` }
    }
  }
}
</script>

<style lang="scss" scoped>
.request-affordability-bar {
  width: 100%;
}

.affordability-text {
  color: #6c757d;
  font-size: 0.875rem;
}

.affordability-bar-wrapper {
  position: relative;
  padding-bottom: 1.25rem;
}

.affordability-bar-wrapper--vertical {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0;
}

.bar-track {
  position: relative;
  height: 1rem;
  border-radius: 0.5rem;
  overflow: visible;
}

.bar-track-vertical {
  width: 0.75rem;
  height: 3.5rem;
  min-height: 3.5rem;
  border-radius: 0.25rem;
  flex-shrink: 0;
}

.bar-marker {
  position: absolute;
  top: -0.25rem;
  bottom: -0.25rem;
  width: 2px;
  background: #212529;
  transform: translateX(-50%);
}

.bar-marker-vertical {
  top: auto;
  left: -0.125rem;
  right: -0.125rem;
  width: auto;
  height: 2px;
  transform: translateY(50%);
}

.bar-label {
  position: absolute;
  bottom: 0;
  transform: translateX(-50%);
  font-size: 0.75rem;
  color: #212529;
  white-space: nowrap;
}

.bar-label-vertical {
  position: static;
  transform: none;
}
</style>
